import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { RequestListNotifications } from '@/schema'
import { WEBPUBSUB_TOKEN_REFRESH_BEFORE_EXPIRY_MS } from '@/utils/constants.ts'

import {
  joinGroups,
  leaveGroups,
  negotiateConnection,
  notificationApi,
} from '../services'
import { useNotificationDrawerStore } from '../store'
import { JoinGroupsRequest, LeaveGroupsRequest } from '../types'

export const useNegotiateConnection = (enabled = false, sessionId?: string) => {
  return useQuery({
    queryKey: ['notification', 'pubsub', 'negotiate', sessionId],
    queryFn: () => negotiateConnection(),
    enabled,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: WEBPUBSUB_TOKEN_REFRESH_BEFORE_EXPIRY_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  })
}

export const useJoinGroups = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: JoinGroupsRequest) => joinGroups(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notification', 'pubsub', 'groups'],
      })
    },
  })
}

export const useLeaveGroups = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: LeaveGroupsRequest) => leaveGroups(request),
    onSuccess: () => {
      if (queryClient) {
        queryClient.invalidateQueries({
          queryKey: ['notification', 'pubsub', 'groups'],
        })
      }
    },
  })
}

/**
 * Hook to fetch a list of notifications with filtering, pagination, and search
 *
 * @param params - Request parameters for listing notifications
 * @param options - React Query options
 * @returns Query result with notifications data
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useListNotifications({
 *   filter_by: {
 *     notification_type: [NotificationType.Mention],
 *     status: ['unread']
 *   },
 *   limit: 20,
 *   offset: 0,
 *   sort_by: '-created_at'
 * })
 * ```
 */
export const useListNotifications = (
  params: RequestListNotifications = {},
  options: {
    enabled?: boolean
    refetchInterval?: number
  } = {}
) => {
  const { enabled = true, ...queryOptions } = options

  // Create a stable query key that properly serializes nested objects
  const queryKey = [
    'notifications',
    'list',
    {
      offset: params.offset,
      limit: params.limit,
      sort_by: params.sort_by,
      q: params.q,
      filter_by: params.filter_by
        ? {
            notification_type: params.filter_by.notification_type?.sort(),
            status: params.filter_by.status?.sort(),
          }
        : undefined,
    },
  ]

  return useQuery({
    queryKey,
    queryFn: () => notificationApi.listNotifications(params),
    enabled,
    ...queryOptions,
  })
}

export const useMarkAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationApi.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      })
    },
  })
}

export const useMarkAsUnread = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) =>
      notificationApi.markAsUnread(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      })
    },
  })
}

export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => notificationApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
      })
    },
  })
}

/**
 * Hook to fetch notifications with infinite scroll pagination
 *
 * @param params - Request parameters for listing notifications (without offset)
 * @param options - React Query options
 * @returns Infinite query result with notifications data
 *
 * @example
 * ```typescript
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage,
 *   isLoading
 * } = useInfiniteNotifications({
 *   filter_by: {
 *     notification_type: [NotificationType.Mention],
 *     status: ['unread']
 *   },
 *   limit: 20,
 *   sort_by: '-created_at'
 * })
 * ```
 */
export const useInfiniteNotifications = (
  params: Omit<RequestListNotifications, 'offset'> = {},
  options: {
    enabled?: boolean
    refetchInterval?: number
  } = {}
) => {
  const { enabled = true, ...queryOptions } = options

  // Create a stable query key
  const queryKey = [
    'notifications',
    'infinite',
    {
      limit: params.limit,
      sort_by: params.sort_by,
      q: params.q,
      filter_by: params.filter_by
        ? {
            notification_type: params.filter_by.notification_type?.sort(),
            status: params.filter_by.status?.sort(),
          }
        : undefined,
    },
  ]

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 0 }) =>
      notificationApi.listNotifications({
        ...params,
        offset: pageParam,
      }),
    enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      // Return the next offset if there are more pages
      if (lastPage.pagination?.has_next) {
        // Calculate next offset: current page * page_size
        return lastPage.pagination.page * (lastPage.pagination.page_size ?? 20)
      }
      return undefined
    },
    ...queryOptions,
  })
}

/**
 * Custom hook that wraps notification drawer store actions
 * React Query will automatically handle refetching when query keys change
 */
export const useNotificationDrawerActions = () => {
  const store = useNotificationDrawerStore()

  // No need to invalidate queries - React Query will automatically fetch
  // new data when the query key changes (which happens when activeTab or filterType changes)
  return store
}
